import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge } from '@/backoffice/components/ui';
import { formatCurrency } from '@/backoffice/utils';
import {
  fetchDriverRoute,
  type DriverRouteData,
  type DriverRouteStop,
} from '@/domains/driver/services/driver.service';
import {
  Route,
  MapPin,
  Navigation,
  Clock,
  Truck,
  Package,
  Smartphone,
  Map,
  Loader2,
} from 'lucide-react';

export function ProRoutePage() {
  const [route, setRoute] = useState<DriverRouteData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDriverRoute()
      .then(setRoute)
      .catch(() => setRoute(null))
      .finally(() => setLoading(false));
  }, []);

  const routeStops = route?.stops ?? [];

  if (loading) {
    return (
      <DashboardLayout userType="pro_driver" title="Feuille de route">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="pro_driver" title="Feuille de route">
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Route className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-blue-700">Distance totale</p>
              <p className="text-xl font-semibold text-blue-900">{route?.total_distance_km ?? 0} km</p>
            </div>
          </div>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-emerald-700">Durée estimée</p>
              <p className="text-xl font-semibold text-emerald-900">{route?.total_duration_min ?? 0} min</p>
            </div>
          </div>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-purple-700">Arrêts</p>
              <p className="text-xl font-semibold text-purple-900">{route?.stops_count ?? 0}</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="h-[500px] bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Map className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Carte de l'itinéraire</p>
              <p className="text-sm text-gray-500">Tracé optimisé des arrêts</p>
            </div>
          </div>

          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
            <path
              d="M20 70 L35 55 L50 40 L65 50 L80 30"
              stroke="#3B82F6"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
            />
            {routeStops.map((stop, i) => {
              const x = 20 + i * 15;
              const y = 70 - i * 10;
              return (
                <circle
                  key={stop.id}
                  cx={x}
                  cy={y}
                  r="4"
                  fill={stop.type === 'start' ? '#10B981' : stop.type === 'pickup' ? '#3B82F6' : '#EF4444'}
                />
              );
            })}
          </svg>

          {route?.maps_url && (
            <a href={route.maps_url} target="_blank" rel="noopener noreferrer" className="absolute bottom-4 right-4">
              <Button leftIcon={<Navigation className="w-4 h-4" />}>Navigation GPS</Button>
            </a>
          )}
        </Card>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Liste des arrêts</h3>
            {route?.maps_url && (
              <a href={route.maps_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" leftIcon={<Smartphone className="w-4 h-4" />}>
                  Ouvrir dans Maps
                </Button>
              </a>
            )}
          </div>

          {routeStops.length === 0 ? (
            <Card>
              <p className="text-center text-gray-500 py-8">Aucune mission en cours pour construire un itinéraire.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {routeStops.map((stop, index) => (
                <RouteStopCard key={stop.id} stop={stop} isLast={index === routeStops.length - 1} />
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function RouteStopCard({ stop, isLast }: { stop: DriverRouteStop; isLast: boolean }) {
  const iconColors = {
    start: 'bg-emerald-100 text-emerald-600',
    pickup: 'bg-blue-100 text-blue-600',
    dropoff: 'bg-red-100 text-red-600',
  };

  return (
    <div className="relative">
      <Card className="flex items-start gap-3">
        <div className="flex flex-col items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconColors[stop.type]}`}>
            {stop.type === 'start' ? <Truck className="w-5 h-5" /> :
             stop.type === 'pickup' ? <Package className="w-5 h-5" /> :
             <MapPin className="w-5 h-5" />}
          </div>
          {!isLast && <div className="w-0.5 h-12 bg-gray-200 my-2" />}
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">{stop.label}</p>
              <p className="text-sm text-gray-600">{stop.address}</p>
            </div>
            {stop.order && (
              <Badge variant={stop.type === 'pickup' ? 'primary' : 'danger'}>
                {stop.order.code}
              </Badge>
            )}
          </div>

          {stop.order && (
            <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">{stop.order.package_description}</span>
                <span className="font-semibold text-emerald-600">{formatCurrency(stop.order.price)}</span>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
